import { SeedFilter } from '@/hooks/useSeeds';
import { SeedType } from '@/sections/seeds/types';

/**
 * Seed URL parameter utilities for shareability and deep linking
 *
 * Manages minimal URL state for basic filters to enable:
 * - Shareable links with filters applied
 * - Deep linking to filtered views
 * - Browser history integration
 *
 * Note: Complex filters remain in localStorage; only basic filters
 * are synced to URL for shareability.
 */
export const seedUrlParams = {
  /**
   * Read basic filters from URL parameters (for deep linking)
   *
   * @returns Partial filter state from URL parameters
   */
  read: (): Partial<SeedFilter> => {
    const params = new URLSearchParams(window.location.search);

    const filters: Partial<SeedFilter> = {};

    const searchParam = params.get('search');
    if (searchParam) {
      filters.search = searchParam;
    }

    const typeParam = params.get('type');
    if (
      typeParam &&
      ['domain', 'ip', 'webapplication', 'preseed'].includes(typeParam)
    ) {
      filters.type = typeParam as SeedType;
    }

    return filters;
  },

  /**
   * Write basic filters to URL (for shareability)
   *
   * Only syncs minimal filter state to keep URLs clean and shareable.
   * Complex filters (advanced filters, etc.) remain in localStorage.
   *
   * @param filters - Partial filter state to sync to URL
   */
  write: (filters: Partial<SeedFilter>) => {
    const params = new URLSearchParams();

    // Only add non-empty basic filters to URL
    if (filters.search && filters.search.trim()) {
      params.set('search', filters.search);
    }

    if (filters.type) {
      params.set('type', filters.type);
    }

    // Update URL without triggering navigation
    const newSearch = params.toString();
    if (newSearch) {
      const newUrl = `${window.location.pathname}?${newSearch}`;
      if (window.location.href !== newUrl) {
        window.history.replaceState({}, '', newUrl);
      }
    } else {
      // Clear search params if none exist
      if (window.location.search) {
        window.history.replaceState({}, '', window.location.pathname);
      }
    }
  },

  /**
   * Clear seed filter URL parameters (preserves drawer params)
   */
  clear: () => {
    if (window.location.search) {
      const params = new URLSearchParams(window.location.search);

      // Only remove seed filter params, preserve drawer keys
      params.delete('search');
      params.delete('type');

      const newSearch = params.toString();
      const newUrl = newSearch
        ? `${window.location.pathname}?${newSearch}`
        : window.location.pathname;

      window.history.replaceState({}, '', newUrl);
    }
  },
};
